/**
 * Calendar-day helpers. Invoice dates come from the API as either "YYYY-MM-DD"
 * or "YYYY-MM-DDT00:00:00Z"; the calendar date is the part before the "T".
 * We deliberately compare calendar dates, not timestamps, so an invoice due
 * "2026-06-15" is never off by one because of the viewer's timezone.
 */

const YMD = /^(\d{4})-(\d{2})-(\d{2})/;
const MS_PER_DAY = 86_400_000;

export function toYmd(value: string | Date): string {
  if (typeof value === 'string') {
    const match = YMD.exec(value);
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return toYmd(parsed);
  }
  const m = String(value.getMonth() + 1).padStart(2, '0');
  const d = String(value.getDate()).padStart(2, '0');
  return `${value.getFullYear()}-${m}-${d}`;
}

/** Splits a "YYYY-MM-DD" string (as returned by toYmd) into numbers. */
export function ymdParts(ymd: string): [number, number, number] {
  const [y = 0, m = 1, d = 1] = ymd.split('-').map(Number);
  return [y, m, d];
}

function utcDay(ymd: string): number {
  const [y, m, d] = ymdParts(ymd);
  return Date.UTC(y, m - 1, d);
}

/** Whole days from `from` to `to` (positive when `to` is later). NaN if either date is unreadable. */
export function daysBetween(from: string, to: string): number {
  const a = toYmd(from);
  const b = toYmd(to);
  if (!a || !b) return Number.NaN;
  return Math.round((utcDay(b) - utcDay(a)) / MS_PER_DAY);
}

export function todayYmd(): string {
  return toYmd(new Date());
}
